import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {Message} from "primereact/message";
import BrandLogo from "../system/components/brand-logo";
import api, {normalizeApiError} from "../system/api";
import {useTranslate} from "../system/useTranslate";
import {saveUser} from "./auth-slice";
import {buildAccountSetupPayload, isAccountSetupFormValid} from "./account-setup";

export function UpdateUserProfile({onCreated}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const passwordRequired = Boolean(user?.requiresPasswordSetup);
    const [form, setForm] = useState({
        firstname: user?.firstname || "",
        lastname: user?.lastname || "",
        fullname: user?.fullname || "",
        gender: user?.gender || null,
        password: "",
        passwordConfirmation: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const update = (patch) => setForm((current) => ({...current, ...patch}));
    const submit = async (event) => {
        event.preventDefault();
        if (loading) return;
        setLoading(true);
        setError(null);
        try {
            const payload = buildAccountSetupPayload(form, passwordRequired);
            const response = await api.put("/updateUser", payload);
            const updatedUser = {...user, ...response.data};
            dispatch(saveUser(updatedUser));
            onCreated?.(updatedUser);
        } catch (requestError) {
            setError((requestError?.normalizedError || normalizeApiError(requestError)).message);
        } finally {
            setLoading(false);
        }
    };

    const valid = isAccountSetupFormValid(form, passwordRequired);

    return (
        <form className="w-full md:w-8 lg:w-6 flex flex-column gap-3" onSubmit={submit}>
            <div className="flex flex-column align-items-center text-center gap-3 mb-2">
                <BrandLogo className="w-12rem h-auto"/>
                <h1 className="text-xl m-0">{translate("profile-onboarding.title")}</h1>
                <span className="text-xs text-600">{translate("profile-onboarding.description")}</span>
            </div>
            <InputText value={form.firstname} onChange={(event) => {
                const firstname = event.target.value;
                update({firstname, fullname: `${firstname} ${form.lastname}`.trim()});
            }} placeholder={translate("firstname-placeholder")} autoFocus/>
            <InputText value={form.lastname} onChange={(event) => {
                const lastname = event.target.value;
                update({lastname, fullname: `${form.firstname} ${lastname}`.trim()});
            }} placeholder={translate("lastname-placeholder")}/>
            <InputText value={form.fullname} onChange={(event) => update({fullname: event.target.value})} placeholder={translate("fullname")}/>
            <Dropdown value={form.gender} options={["male", "female"]} onChange={(event) => update({gender: event.value})} placeholder={translate("select-gender-placeholder")}/>
            {passwordRequired && (
                <div className="flex flex-column gap-3 mt-2">
                    <div className="flex flex-column gap-1">
                        <span className="font-bold text-sm">{translate("profile-onboarding.password-title")}</span>
                        <span className="text-xs text-600">{translate("profile-onboarding.password-description")}</span>
                    </div>
                    <Password
                        value={form.password}
                        onChange={(event) => update({password: event.target.value})}
                        placeholder={translate("new-password")}
                        inputClassName="w-full ltr"
                        className="w-full"
                        toggleMask
                        feedback={false}
                        autoComplete="new-password"
                    />
                    <Password
                        value={form.passwordConfirmation}
                        onChange={(event) => update({passwordConfirmation: event.target.value})}
                        placeholder={translate("confirm-password")}
                        inputClassName="w-full ltr"
                        className="w-full"
                        toggleMask
                        feedback={false}
                        autoComplete="new-password"
                    />
                    {form.passwordConfirmation && form.password !== form.passwordConfirmation && (
                        <Message severity="error" text={translate("auth.account-setup.password-mismatch")}/>
                    )}
                </div>
            )}
            {error && <Message severity="error" text={error}/>}
            <Button type="submit" label={translate("init-profile-action-finish")} loading={loading} disabled={!valid}/>
        </form>
    );
}

export function Init({onCreated}) {
    return <div className="w-full h-full overflow-auto surface-0 flex align-items-center justify-content-center p-3"><UpdateUserProfile onCreated={onCreated}/></div>;
}

export default Init;
